import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ButtonBase from '../components/ButtonBase';
import CaretRightIcon from '../components/Icons/CaretRight';
import LanguageItem from '../components/LanguageItem';
import { useStores } from '../stores';
import {
  MENU_ITEM_ACTION,
  MENU_ITEM_LANGUAGE,
  MENU_ITEM_LINK,
  MENU_ITEM_SPACER
} from '../utilities';
import useMenuFunctions from './useMenuFunctions';

export default function useMenuItemRenderer() {
  const { network } = useStores();
  const { executeFunction } = useMenuFunctions();
  const { t } = useTranslation();
  const disabled = network.isOffline || network.isSlowConnection;

  const renderLanguageItem = React.useCallback(
    (item, idx) => (
      <LanguageItem
        key={idx}
        label={item.label}
        name={item.name}
        isDefault={item.default}
        onClick={() => executeFunction(item.functionName, item.functionArgs)}
        disabled={disabled}
      />
    ),
    [disabled, executeFunction]
  );

  const renderer = React.useCallback(
    (item, idx) => {
      const hasChildren = item.children && item.children.length > 0;

      switch (item.type) {
        case MENU_ITEM_ACTION:
          return (
            <Button
              key={idx}
              type="button"
              onClick={() =>
                hasChildren ? null : executeFunction(item.functionName, item.functionArgs)
              }
              disabled={disabled}
            >
              {t(item.label)}
              {hasChildren && <StyledCaretRightIcon />}
            </Button>
          );
        case MENU_ITEM_LANGUAGE:
          return renderLanguageItem(item, idx);
        case MENU_ITEM_LINK:
          return (
            <Link key={idx} href={item.url || '#'} target="_blank" rel="noopener noreferrer">
              {t(item.label)}
            </Link>
          );
        case MENU_ITEM_SPACER:
          return <Line key={idx} />;
        default:
          return null;
      }
    },
    [disabled, executeFunction, renderLanguageItem, t]
  );

  return renderer;
}

const Button = styled(ButtonBase)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 30px;
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  white-space: inherit;
  text-align: left;
  color: inherit;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.4, 0, 1, 1);
  outline: none;
`;

const StyledCaretRightIcon = styled(CaretRightIcon)`
  width: 6px;
  height: 20px;
  fill: inherit;
`;

const Link = styled.a`
  display: block;
  width: 100%;
  padding: 0 30px;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  text-align: left;
  text-decoration: none;
  white-space: inherit;
  color: inherit;
  transition: color 0.2s cubic-bezier(0.4, 0, 1, 1);
`;

const Line = styled.div`
  position: relative;
  height: 21px;
  padding: 0 30px;
  width: 100%;

  :after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    display: block;
    width: calc(100% - 60px);
    border-bottom: 1px solid #c4d1d8;
    transform: translate(-50%, 50%);
  }
`;
